import { useAtomValue, useSetAtom, useAtom } from 'jotai'
import { useRef, useEffect, useCallback, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as LabelGeneratorStore from '../../stores/LabelGeneratorStore'
import { hasEUMarket, isEUCountry, shouldRequireUKRep } from '../../stores/fieldRegistry'
import { currentUserQueryAtom } from '../../auth'
import { trackLabelPrint, trackFeedbackSubmitted } from '../../lib/posthog'
import { apiBase } from 'txom-frontend-libs'
import { pageLinks } from '../../pageLinks'
import { AppShell } from '../../components/AppShell'

// ============================================================================
// Icons
// ============================================================================

function PrinterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.25 7.034l.016.003" />
    </svg>
  )
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
    </svg>
  )
}

function MaximizeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9m11.25-5.25v4.5m0-4.5h-4.5m4.5 0L15 9m-11.25 11.25v-4.5m0 4.5h4.5m-4.5 0L9 15m11.25 5.25v-4.5m0 4.5h-4.5m4.5 0L15 15" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  )
}

// ============================================================================
// Safety Warning Presets
// ============================================================================

const SAFETY_WARNING_PRESETS: { label: string; text: string; condition: (d: LabelGeneratorStore.LabelData) => boolean }[] = [
  // Electronics (battery)
  { label: 'Button cell battery', text: 'Contains button cell battery. Keep out of reach of children.', condition: (d) => d.productCategory === 'electronics' && d.containsBattery },
  { label: 'Electric shock risk', text: 'Risk of electric shock. Do not open.', condition: (d) => d.productCategory === 'electronics' },
  { label: 'No water/moisture', text: 'Do not expose to water or moisture.', condition: (d) => d.productCategory === 'electronics' },
  { label: 'Indoor use only', text: 'For indoor use only.', condition: (d) => d.productCategory === 'electronics' },
  // Electronics (radio, US)
  { label: 'FCC Part 15', text: 'This device complies with Part 15 of the FCC Rules.', condition: (d) => d.productCategory === 'electronics' && d.radio && d.targetMarkets.includes('US') },
  // General + Electronics
  { label: 'Keep from children', text: 'Keep out of reach of children.', condition: (d) => d.productCategory === 'general' || d.productCategory === 'electronics' },
  { label: 'Adult use only', text: 'For adult use only.', condition: (d) => d.productCategory === 'general' },
  // Market-specific
  { label: 'California Prop 65', text: 'This product can expose you to chemicals known to the State of California to cause cancer. For more info: www.P65Warnings.ca.gov', condition: (d) => d.targetMarkets.includes('US') },
  // Toys
  { label: 'Contains small magnets', text: 'Contains small magnets.', condition: (d) => d.productCategory === 'toy' },
  { label: 'Adult supervision required', text: 'Adult supervision required.', condition: (d) => d.productCategory === 'toy' },
]

// ============================================================================
// Simple Markdown-like rendering (bold only)
// ============================================================================

function renderTextContent(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <span key={i} className="font-semibold">
          {part.slice(2, -2)}
        </span>
      )
    }
    return <span key={i}>{part}</span>
  })
}


// ============================================================================
// Top Bar
// ============================================================================

function SaveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
    </svg>
  )
}

function SaveProjectButton() {
  const navigate = useNavigate()
  const { data: user, isPending } = useAtomValue(currentUserQueryAtom)
  const currentLabelId = useAtomValue(LabelGeneratorStore.currentLabelIdAtom)
  const labelData = useAtomValue(LabelGeneratorStore.labelDataAtom)
  const chatMessages = useAtomValue(LabelGeneratorStore.chatMessagesAtom)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // If already editing a saved label, show "Saved" indicator
  if (currentLabelId) {
    return (
      <Link
        to={pageLinks.Dashboard()}
        className="px-4 h-9 border border-[#E5E5E5] rounded-lg text-sm font-medium text-[#52525B] flex items-center gap-2 hover:bg-[#FAFAFA] transition-colors"
      >
        My Labels
      </Link>
    )
  }

  const handleSave = async () => {
    // If not logged in, redirect to login with return URL
    if (!user) {
      const returnUrl = encodeURIComponent(window.location.pathname)
      navigate(`${pageLinks.Login()}?redirect=${returnUrl}`)
      return
    }

    setSaving(true)
    try {
      // Create a new label in the database
      const res = await fetch('/api/DashboardStore/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
        credentials: 'include',
      })
      const data = await res.json() as { id: string }

      // Save the label data
      await fetch('/api/LabelStorageStore/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: data.id,
          name: labelData.productName || 'Untitled Label',
          labelData,
          chatHistory: chatMessages,
        }),
        credentials: 'include',
      })

      setSaved(true)
      // Navigate to the saved label
      navigate(pageLinks.LabelGenerator(data.id))
    } finally {
      setSaving(false)
    }
  }

  if (isPending) return null

  return (
    <button
      onClick={handleSave}
      disabled={saving}
      className="px-4 h-9 border border-[#E5E5E5] rounded-lg text-sm font-medium text-[#18181B] flex items-center gap-2 hover:bg-[#FAFAFA] transition-colors cursor-pointer disabled:opacity-50"
    >
      <SaveIcon className="size-4" />
      {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Project'}
    </button>
  )
}

function LabelTopBarActions() {
  const labelType = useAtomValue(LabelGeneratorStore.labelTypeAtom)
  const isCompliant = useAtomValue(LabelGeneratorStore.isCompliantAtom)
  const hasStarted = useAtomValue(LabelGeneratorStore.hasStartedAtom)
  const labelData = useAtomValue(LabelGeneratorStore.labelDataAtom)
  const dimensions = useAtomValue(LabelGeneratorStore.dimensionsAtom)

  // For UPC mode, derive readiness from whether UPC code is filled
  const isUPCReady = labelType === 'upc' && !!labelData.upcCode && labelData.upcCode.replace(/\D/g, '').length === 12
  const isUPCStarted = labelType === 'upc' && !!labelData.upcCode
  const effectiveCompliant = labelType === 'upc' ? isUPCReady : isCompliant
  const effectiveStarted = labelType === 'upc' ? isUPCStarted : hasStarted

  const handlePrint = useCallback(() => {
    const w = dimensions.width   // mm
    const h = dimensions.height  // mm
    const DESIGN_WIDTH = 380
    const designHeight = Math.round(DESIGN_WIDTH * h / w)
    // In CSS print context: 1in = 25.4mm = 96px → 1mm = 96/25.4 px
    const printScale = (w * 96) / (25.4 * DESIGN_WIDTH)

    // Printer compensation: offset + bleed to fill thermal printer non-printable margins
    const comp = 2 // mm

    const styleId = 'print-label-style'
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = styleId
      document.head.appendChild(styleEl)
    }

    styleEl.textContent = `
      @page {
        size: ${w}mm ${h}mm;
        margin: 0;
      }
      @media print {
        /* 1) html/body = page size, zero all margins */
        html, body {
          width: ${w}mm !important;
          height: ${h}mm !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }
        /* 2) Hide everything except label */
        body * { visibility: hidden !important; }
        #print-label, #print-label * { visibility: visible !important; }
        /* 3) Label: negative offset + bleed to compensate printer margins */
        #print-label {
          position: fixed !important;
          left: -${comp}mm !important;
          top: -${comp}mm !important;
          width: calc(${w}mm + ${comp * 2}mm) !important;
          height: calc(${h}mm + ${comp * 2}mm) !important;
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          background: white !important;
          overflow: hidden !important;
          transform: none !important;
          zoom: 1 !important;
          border: none !important;
        }
        /* Hide the screen border overlay */
        #print-label > div:first-child {
          display: none !important;
        }
        /* No border on print output */
        #print-label::after {
          display: none !important;
        }
        /* 4) Content: design-width layout, scaled to fill label */
        #print-label-content {
          position: absolute !important;
          left: ${comp}mm !important;
          top: ${comp}mm !important;
          width: ${DESIGN_WIDTH}px !important;
          height: ${designHeight}px !important;
          transform: scale(${printScale}) !important;
          transform-origin: top left !important;
          zoom: 1 !important;
          z-index: 1 !important;
        }
        /* 5) ShrinkToFit: preserve inline scale so content stays within bounds */
        /* 6) Kill any preview wrapper transforms */
        .previewWrapper, .canvasWrapper {
          transform: none !important;
          zoom: 1 !important;
        }
      }
    `

    trackLabelPrint()

    // Wait for fonts to load, then print with 2x rAF to ensure layout is stable
    document.fonts?.ready.then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.print()
        })
      })
    })
  }, [dimensions])

  return (
    <>
      <FeedbackPopover />
      <button
        onClick={handlePrint}
        className="px-4 h-9 border border-[#E5E5E5] rounded-lg text-sm font-medium text-[#18181B] flex items-center gap-2 hover:bg-[#FAFAFA] transition-colors cursor-pointer"
      >
        <PrinterIcon className="size-4" />
        Print PDF
      </button>
      {effectiveCompliant && effectiveStarted ? (
        <div className="px-3 h-8 rounded-full bg-[#F0FDF4] border border-[#BBF7D0] text-[#16A34A] text-sm font-medium flex items-center gap-1.5">
          <CheckCircleIcon className="size-4" />
          Ready
        </div>
      ) : (
        <div className="px-3 h-8 rounded-full bg-[#F5F5F5] border border-[#E5E5E5] text-[#A1A1AA] text-sm font-medium flex items-center gap-1.5">
          {effectiveStarted ? 'Incomplete' : 'Not started'}
        </div>
      )}
    </>
  )
}

// ============================================================================
// Chat Panel
// ============================================================================

function ChatMessages() {
  const messages = useAtomValue(LabelGeneratorStore.chatMessagesAtom)
  const isTyping = useAtomValue(LabelGeneratorStore.isTypingAtom)
  const step = useAtomValue(LabelGeneratorStore.onboardingStepAtom)
  const labelType = useAtomValue(LabelGeneratorStore.labelTypeAtom)
  const scrollRef = useRef<HTMLDivElement>(null)
  const lastMsgRef = useRef<HTMLDivElement>(null)

  // Scroll so the message BEFORE the newest is at the top of the viewport.
  // This gives the user context (the prior message) plus breathing room,
  // with the newest AI message visible below and field cards further down.
  useEffect(() => {
    if (lastMsgRef.current) {
      lastMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [messages, isTyping, step])

  // Target the message before the last one (or the first if only 1-2 messages)
  const scrollTargetIdx = Math.max(0, messages.length - 2)

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map((msg, idx) => (
        <div
          key={msg.id}
          ref={idx === scrollTargetIdx ? lastMsgRef : undefined}
          className={`${msg.role === 'user' ? 'flex justify-end' : ''} ${idx === scrollTargetIdx ? 'scroll-mt-4' : ''}`}
        >
          <div
            className={
              msg.role === 'user'
                ? 'max-w-[85%] px-3 py-2 rounded-lg bg-[#F5F5F5] text-[13px] text-[#18181B] leading-relaxed'
                : 'text-[13px] text-[#18181B] leading-relaxed'
            }
          >
            {msg.content.split('\n').map((line, i) => (
              <p key={i} className={line === '' ? 'h-2' : ''}>
                {renderTextContent(line)}
              </p>
            ))}
          </div>
        </div>
      ))}
      {isTyping && (
        <div className="flex items-center gap-1 text-[#A1A1AA] text-xs">
          <span className="animate-pulse">Typing</span>
          <span className="animate-pulse">...</span>
        </div>
      )}
      {step === 'done' && (labelType === 'upc' ? <UPCFieldsPanel /> : <LabelFieldsPanel />)}
    </div>
  )
}

function UPCFieldsPanel() {
  const [data, setData] = useAtom(LabelGeneratorStore.labelDataAtom)
  const triggerAutoSave = useSetAtom(LabelGeneratorStore.triggerAutoSaveAtom)

  const update = useCallback((field: string, value: string) => {
    setData({ ...data, [field]: value })
    triggerAutoSave()
  }, [data, setData, triggerAutoSave])

  return (
    <div className="space-y-2.5 animate-[fadeSlideIn_0.4s_ease-out]">
      <FieldCard
        title="UPC Barcode"
        rationale="A 12-digit UPC-A barcode for retail distribution and point-of-sale scanning."
      >
        <FieldInput
          label="UPC Code"
          value={data.upcCode}
          onChange={(v) => update('upcCode', v)}
          placeholder="12-digit barcode number"
        />
      </FieldCard>

      <FieldCard
        title="Additional Details (Optional)"
        rationale="Country of origin and additional text are optional but commonly included on UPC labels."
      >
        <FieldInput
          label="Country of Origin"
          value={data.countryOfOrigin}
          onChange={(v) => update('countryOfOrigin', v)}
          placeholder="e.g. China"
        />
        <FieldInput
          label="Additional Info"
          value={data.additionalInfo}
          onChange={(v) => update('additionalInfo', v)}
          placeholder="e.g. Net Wt. 8 oz, Lot #12345"
        />
      </FieldCard>
    </div>
  )
}

function OnboardingLabelTypeStep() {
  const selectLabelType = useSetAtom(LabelGeneratorStore.selectLabelTypeAtom)

  return (
    <div className="px-4 pb-3">
      <div className="flex flex-col gap-2">
        {LabelGeneratorStore.LABEL_TYPE_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            onClick={() => selectLabelType(opt.id)}
            className="px-4 py-3 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-left hover:bg-[#EBEBEB] hover:border-[#71717A] cursor-pointer transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-[15px]">{opt.icon}</span>
              <span className="text-[13px] font-medium text-[#18181B]">{opt.label}</span>
            </div>
            <p className="text-[11px] text-[#71717A] mt-1 ml-6">{opt.description}</p>
          </button>
        ))}
      </div>
    </div>
  )
}

function OnboardingProductCategoryStep() {
  const selectCategory = useSetAtom(LabelGeneratorStore.selectProductCategoryAtom)

  return (
    <div className="px-4 pb-3">
      <div className="flex flex-col gap-1.5">
        {LabelGeneratorStore.PRODUCT_CATEGORY_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            onClick={() => selectCategory(opt.id)}
            className="px-3 py-2.5 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[13px] text-[#18181B] hover:bg-[#EBEBEB] hover:border-[#71717A] cursor-pointer transition-colors text-left"
          >
            {opt.icon} {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function OnboardingMarketsStep() {
  const selectedMarkets = useAtomValue(LabelGeneratorStore.onboardingMarketsAtom)
  const toggleMarket = useSetAtom(LabelGeneratorStore.toggleMarketAtom)
  const confirmMarkets = useSetAtom(LabelGeneratorStore.confirmMarketsAtom)

  return (
    <div className="px-4 pb-3 space-y-2">
      <div className="grid grid-cols-2 gap-1.5">
        {LabelGeneratorStore.TARGET_MARKET_OPTIONS.map((m) => {
          const isSelected = selectedMarkets.has(m.id)
          return (
            <button
              key={m.id}
              onClick={() => toggleMarket(m.id)}
              className={`px-3 py-2 rounded-lg border text-[13px] cursor-pointer transition-colors text-left ${
                isSelected
                  ? 'bg-[#18181B] border-[#18181B] text-white'
                  : 'bg-[#F5F5F5] border-[#E5E5E5] text-[#18181B] hover:bg-[#EBEBEB]'
              }`}
            >
              {m.flag} {m.label}
            </button>
          )
        })}
      </div>
      {selectedMarkets.size > 0 && (
        <button
          onClick={() => confirmMarkets()}
          className="w-full py-2 rounded-lg bg-[#18181B] text-white text-[13px] font-medium cursor-pointer hover:bg-[#27272A] transition-colors"
        >
          Continue
        </button>
      )}
    </div>
  )
}

function OnboardingYesNoStep({ onSelect }: { onSelect: (value: boolean) => void }) {
  return (
    <div className="px-4 pb-3 flex gap-2">
      <button
        onClick={() => onSelect(true)}
        className="flex-1 py-2 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[13px] text-[#18181B] hover:bg-[#EBEBEB] cursor-pointer transition-colors"
      >
        Yes
      </button>
      <button
        onClick={() => onSelect(false)}
        className="flex-1 py-2 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[13px] text-[#18181B] hover:bg-[#EBEBEB] cursor-pointer transition-colors"
      >
        No
      </button>
    </div>
  )
}

function OnboardingFRPackagingStep() {
  const selectFRPackaging = useSetAtom(LabelGeneratorStore.selectFRPackagingAtom)

  return (
    <div className="px-4 pb-3 space-y-2">
      <div className="flex flex-col gap-1.5">
        {LabelGeneratorStore.FR_PACKAGING_TEMPLATE_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            onClick={() => selectFRPackaging(opt.id)}
            className="px-3 py-2.5 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[13px] text-[#18181B] hover:bg-[#EBEBEB] hover:border-[#71717A] cursor-pointer transition-colors text-left"
          >
            {opt.icon} {opt.label}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-[#A1A1AA] leading-snug">Choose the option that best matches your retail packaging.</p>
    </div>
  )
}

function OnboardingPanel() {
  const step = useAtomValue(LabelGeneratorStore.onboardingStepAtom)
  const isTyping = useAtomValue(LabelGeneratorStore.isTypingAtom)
  const selectRadio = useSetAtom(LabelGeneratorStore.selectRadioAtom)
  const selectProductType = useSetAtom(LabelGeneratorStore.selectProductTypeAtom)
  const selectToySmallParts = useSetAtom(LabelGeneratorStore.selectToySmallPartsAtom)
  const selectToyAgeRestriction = useSetAtom(LabelGeneratorStore.selectToyAgeRestrictionAtom)

  if (step === 'done' || isTyping) return null

  switch (step) {
    case 'labelType': return <OnboardingLabelTypeStep />
    case 'productCategory': return <OnboardingProductCategoryStep />
    case 'productType': return <OnboardingYesNoStep onSelect={selectProductType} />
    case 'toySmallParts': return <OnboardingYesNoStep onSelect={selectToySmallParts} />
    case 'toyAgeRestriction': return <OnboardingYesNoStep onSelect={selectToyAgeRestriction} />
    case 'markets': return <OnboardingMarketsStep />
    case 'radio': return <OnboardingYesNoStep onSelect={selectRadio} />
    case 'frPackaging': return <OnboardingFRPackagingStep />
    default: return null
  }
}

function ChatInput() {
  const [input, setInput] = useAtom(LabelGeneratorStore.chatInputAtom)
  const sendMessage = useSetAtom(LabelGeneratorStore.sendMessageAtom)
  const isTyping = useAtomValue(LabelGeneratorStore.isTypingAtom)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isTyping) return
    sendMessage(input)
  }

  return (
    <div className="px-4 pb-4">
      <form onSubmit={handleSubmit} className="flex items-center gap-2 border border-[#E5E5E5] rounded-lg px-3 h-10">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Message..."
          className="flex-1 text-sm bg-transparent outline-none placeholder:text-[#A1A1AA] text-[#18181B]"
          disabled={isTyping}
        />
        <button
          type="submit"
          disabled={!input.trim() || isTyping}
          className="shrink-0 text-[#A1A1AA] hover:text-[#18181B] disabled:opacity-40 cursor-pointer transition-colors"
        >
          <SendIcon className="size-5" />
        </button>
      </form>
    </div>
  )
}

// ── Inline Label Fields (Chat-Integrated) ──────────────────────────────────
// Editable input fields rendered inline in the chat flow after onboarding.
// Each field card looks like a conversational prompt with rationale, rather
// than a separate form panel. Fields are conditionally shown based on product
// category, markets, and previous answers.

function FieldInput({ label, value, onChange, placeholder, error, description }: {
  label: string
  value: string
  onChange: (val: string) => void
  placeholder?: string
  error?: boolean
  description?: string
}) {
  const [local, setLocal] = useState(value)

  // Sync local state when atom value changes externally (e.g. AI updates)
  useEffect(() => { setLocal(value) }, [value])

  return (
    <div>
      <label className={`block text-[12px] font-medium mb-0.5 ${error ? 'text-[#EF4444]' : 'text-[#52525B]'}`}>{label}</label>
      {description && <p className="text-[12px] text-[#A1A1AA] mb-1.5 leading-snug">{description}</p>}
      <input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { if (local !== value) onChange(local) }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur() } }}
        placeholder={placeholder}
        className={`h-8 px-2.5 border rounded-md text-[13px] bg-white w-full outline-none text-[#18181B] placeholder:text-[#D4D4D8] transition-colors ${
          error
            ? 'border-[#EF4444] focus:border-[#EF4444] focus:ring-1 focus:ring-[#EF4444]/20'
            : 'border-[#E5E5E5] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/20'
        }`}
      />
    </div>
  )
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  )
}

function FieldCard({ title, rationale, children, className }: {
  title: string
  rationale: string
  children: React.ReactNode
  className?: string
}) {
  const [showRationale, setShowRationale] = useState(false)

  return (
    <div className={`rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] ${className ?? ''}`}>
      <div className="px-3 pt-2.5 pb-1.5 flex items-center justify-between">
        <p className="text-[12px] font-semibold text-[#18181B] uppercase tracking-wide">{title}</p>
        <button
          onClick={() => setShowRationale(!showRationale)}
          className="flex items-center gap-0.5 text-[10px] text-[#A1A1AA] hover:text-[#52525B] cursor-pointer transition-colors"
          title="Why is this required?"
        >
          <InfoIcon className="size-3.5" />
          <span>Why?</span>
        </button>
      </div>
      {showRationale && (
        <div className="mx-3 mb-1.5 px-2.5 py-1.5 rounded bg-[#F0F9FF] border border-[#BAE6FD]">
          <p className="text-[12px] text-[#0369A1] leading-snug">{rationale}</p>
        </div>
      )}
      <div className="px-3 pb-3 space-y-3">
        {children}
      </div>
    </div>
  )
}

function EUImporterCard({ data, update, onSkip, isFieldError }: {
  data: LabelGeneratorStore.LabelData
  update: (field: string, value: string) => void
  onSkip: () => void
  isFieldError?: (field: string) => boolean
}) {
  return (
    <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAFA]">
      <div className="px-3 pt-2.5 pb-1.5">
        <p className="text-[11px] font-semibold text-[#18181B] uppercase tracking-wide">EU Importer</p>
      </div>
      <div className="mx-3 mb-2 px-2.5 py-1.5 rounded bg-[#F0F9FF] border border-[#BAE6FD]">
        <p className="text-[12px] text-[#0369A1] leading-snug">
          Non-EU manufacturers selling non-CE products in the EU must list an EU Importer on the label (EU 2019/1020). The label shows an "Imported by:" prefix.
        </p>
      </div>
      <div className="px-3 pb-2 space-y-2">
        <FieldInput label="Importer Name" value={data.euImporterName} onChange={(v) => update('euImporterName', v)} placeholder="Legal company name" error={isFieldError?.('euImporterName')} />
        <FieldInput label="Importer Address" value={data.euImporterAddress} onChange={(v) => update('euImporterAddress', v)} placeholder="Full EU address (street, postal code, city, country)" error={isFieldError?.('euImporterAddress')} />
      </div>
      <div className="px-3 pb-3">
        <button
          onClick={onSkip}
          className="w-full py-2 rounded-md border border-dashed border-[#D4D4D8] text-[12px] text-[#71717A] hover:border-[#A1A1AA] hover:text-[#52525B] cursor-pointer transition-colors flex items-center justify-center gap-1.5"
        >
          <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          Skip — print on a separate label later
        </button>
      </div>
    </div>
  )
}

function EUImporterSkippedCard({ onUnskip }: { onUnskip: () => void }) {
  return (
    <div className="rounded-lg border border-dashed border-[#D4D4D8] bg-[#FAFAFA] px-3 py-2.5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold text-[#A1A1AA] uppercase tracking-wide">EU Importer</p>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5">Skipped — can be printed on a separate label before shipment.</p>
        </div>
        <button
          onClick={onUnskip}
          className="text-[11px] text-[#3B82F6] hover:text-[#2563EB] cursor-pointer font-medium transition-colors"
        >
          Add now
        </button>
      </div>
    </div>
  )
}

// ── Formatting helpers for Verify & Format ──────────────────────────────────

/** Title Case: "bluetooth speaker" → "Bluetooth Speaker" */
function toTitleCase(s: string): string {
  if (!s) return s
  const minorWords = new Set(['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'by', 'in', 'of', 'up', 'as'])
  return s
    .toLowerCase()
    .split(' ')
    .map((w, i) => (i === 0 || !minorWords.has(w)) ? w.charAt(0).toUpperCase() + w.slice(1) : w)
    .join(' ')
}

/** Sentence case: "RISK OF ELECTRIC SHOCK" → "Risk of electric shock" */
function toSentenceCase(s: string): string {
  if (!s) return s
  // Split on sentence boundaries (. ! ?) to handle multi-sentence strings
  return s.replace(/(^|[.!?]\s+)(\w)/g, (_m, prefix: string, ch: string) => prefix + ch.toUpperCase())
    .replace(/^./, (ch) => ch.toUpperCase())
}

/** Normalise a label data object: apply Title Case / Sentence Case per field */
function formatLabelData(data: LabelGeneratorStore.LabelData): Partial<LabelGeneratorStore.LabelData> {
  const updates: Partial<LabelGeneratorStore.LabelData> = {}

  // Title Case fields: names, countries
  const titleCaseFields: (keyof LabelGeneratorStore.LabelData)[] = [
    'productName', 'manufacturerName', 'countryOfOrigin', 'manufacturerCountry',
    'ecRepName', 'euImporterName', 'ukRepName',
  ]
  for (const f of titleCaseFields) {
    const val = data[f]
    if (typeof val === 'string' && val.trim()) {
      const formatted = toTitleCase(val)
      if (formatted !== val) (updates as Record<string, string>)[f] = formatted
    }
  }

  // Sentence Case fields: addresses, warnings
  const sentenceCaseFields: (keyof LabelGeneratorStore.LabelData)[] = [
    'manufacturerAddress', 'ecRepAddress', 'euImporterAddress', 'ukRepAddress',
  ]
  for (const f of sentenceCaseFields) {
    const val = data[f]
    if (typeof val === 'string' && val.trim()) {
      const formatted = toSentenceCase(val.toLowerCase())
      if (formatted !== val) (updates as Record<string, string>)[f] = formatted
    }
  }

  // Uppercase: model/SKU, FCC ID, IC ID (regulatory identifiers are conventionally uppercase)
  const upperFields: (keyof LabelGeneratorStore.LabelData)[] = ['modelRef', 'fccId', 'icId']
  for (const f of upperFields) {
    const val = data[f]
    if (typeof val === 'string' && val.trim()) {
      const formatted = val.toUpperCase()
      if (formatted !== val) (updates as Record<string, string>)[f] = formatted
    }
  }

  // Lowercase: email
  if (data.ecRepEmail && data.ecRepEmail !== data.ecRepEmail.toLowerCase()) {
    updates.ecRepEmail = data.ecRepEmail.toLowerCase()
  }

  return updates
}

function SafetyWarningPresets({ data, onSelect }: {
  data: LabelGeneratorStore.LabelData
  onSelect: (text: string) => void
}) {
  const visiblePresets = SAFETY_WARNING_PRESETS.filter((p) => p.condition(data))

  if (visiblePresets.length === 0) return null

  return (
    <div>
      <label className="block text-[11px] font-medium text-[#52525B] mb-1">Quick Add</label>
      <div className="flex flex-wrap gap-1.5">
        {visiblePresets.map((preset) => {
          const isActive = data.safetyWarning.includes(preset.text)
          return (
            <button
              key={preset.label}
              onClick={() => onSelect(preset.text)}
              className={`px-2.5 py-1 rounded-full border text-[12px] cursor-pointer transition-colors ${
                isActive
                  ? 'bg-[#18181B] border-[#18181B] text-white'
                  : 'bg-[#F5F5F5] border-[#E5E5E5] text-[#18181B] hover:bg-[#EBEBEB] hover:border-[#71717A]'
              }`}
              title={preset.text}
            >
              {preset.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function LabelFieldsPanel() {
  const [data, setData] = useAtom(LabelGeneratorStore.labelDataAtom)
  const triggerAutoSave = useSetAtom(LabelGeneratorStore.triggerAutoSaveAtom)

  const update = useCallback((field: string, value: string) => {
    const updates: Partial<LabelGeneratorStore.LabelData> = { [field]: value }
    // Cross-populate country fields
    if (field === 'countryOfOrigin' && !data.manufacturerCountry) {
      updates.manufacturerCountry = value
    }
    if (field === 'manufacturerCountry' && !data.countryOfOrigin) {
      updates.countryOfOrigin = value
    }
    setData({ ...data, ...updates })
    triggerAutoSave()
  }, [data, setData, triggerAutoSave])

  const handleSkipImporter = useCallback(() => {
    setData({ ...data, euImporterSkipped: true })
    triggerAutoSave()
  }, [data, setData, triggerAutoSave])

  const handleUnskipImporter = useCallback(() => {
    setData({ ...data, euImporterSkipped: false })
    triggerAutoSave()
  }, [data, setData, triggerAutoSave])

  const handlePresetSelect = useCallback((presetText: string) => {
    const current = data.safetyWarning.trim()
    if (current.includes(presetText)) {
      // Deselect: remove the preset text and clean up extra spaces
      const newValue = current.replace(presetText, '').replace(/  +/g, ' ').trim()
      update('safetyWarning', newValue)
    } else {
      // Select: append the preset text
      const newValue = current ? `${current} ${presetText}` : presetText
      update('safetyWarning', newValue)
    }
  }, [data.safetyWarning, update])

  const validationErrors = useAtomValue(LabelGeneratorStore.validationErrorsAtom)
  const [verifyResult, setVerifyResult] = useState<'idle' | 'pass' | 'fail'>('idle')
  const [errorFields, setErrorFields] = useState<Set<string>>(new Set())

  const handleVerify = useCallback(() => {
    // Apply formatting
    const updates = formatLabelData(data)
    if (Object.keys(updates).length > 0) {
      setData({ ...data, ...updates })
      triggerAutoSave()
    }

    // Check validation — highlight missing fields
    if (validationErrors.length > 0) {
      setVerifyResult('fail')
      setErrorFields(new Set(validationErrors.map((e) => e.field)))
    } else {
      setVerifyResult('pass')
      setErrorFields(new Set())
      setTimeout(() => setVerifyResult('idle'), 2500)
    }
  }, [data, setData, triggerAutoSave, validationErrors])

  // Clear error highlight on a field once the user fills it in
  const isFieldError = useCallback((field: string) => errorFields.has(field), [errorFields])

  // ── Conditional visibility ──
  const showManufacturerCountry = hasEUMarket(data) || data.targetMarkets.includes('UK')
  const showFccId = data.targetMarkets.includes('US') && data.radio
  const showIcId = data.targetMarkets.includes('CA') && data.radio
  // Show EU REP whenever EU market is selected and manufacturer is not confirmed
  // to be in the EU — covers both CE-regulated (mandatory) and non-CE (optional/manual).
  const showEURep = hasEUMarket(data) && !isEUCountry(data.manufacturerCountry)
  // Show EU Importer for all product types when EU market is selected and
  // manufacturer is outside the EU — same broad logic as EU REP.
  const showEUImporter = hasEUMarket(data) && !isEUCountry(data.manufacturerCountry)
  const showUKRep = shouldRequireUKRep(data)

  return (
    <div className="space-y-2.5 animate-[fadeSlideIn_0.4s_ease-out]">
      {/* ── Product Info ── */}
      <FieldCard
        title="Product Info"
        rationale="Product name and identifier are required on all compliance labels for traceability and recall compliance in all major markets (US, EU, UK, Canada)."
      >
        <FieldInput label="Product Name" value={data.productName} onChange={(v) => update('productName', v)} placeholder="e.g. Bluetooth Speaker" error={isFieldError('productName')} />
        <FieldInput label="Model / SKU" value={data.modelRef} onChange={(v) => update('modelRef', v)} placeholder="e.g. BS-100" error={isFieldError('modelRef')} />
      </FieldCard>

      {/* ── Manufacturer ── */}
      <FieldCard
        title="Manufacturer"
        rationale="All major markets (US, EU, UK, Canada) require the manufacturer's legal name and physical address on the label. Country of origin must appear as 'Made in [Country]' for customs and trade compliance."
      >
        <FieldInput label="Manufacturer Name" value={data.manufacturerName} onChange={(v) => update('manufacturerName', v)} placeholder="Legal company name" error={isFieldError('manufacturerName')} />
        <FieldInput label="Manufacturer Address" value={data.manufacturerAddress} onChange={(v) => update('manufacturerAddress', v)} placeholder="Full physical address" error={isFieldError('manufacturerAddress')} />
        <FieldInput label="Country of Origin" value={data.countryOfOrigin} onChange={(v) => update('countryOfOrigin', v)} placeholder="e.g. China" error={isFieldError('countryOfOrigin')} />
        {showManufacturerCountry && (
          <FieldInput label="Manufacturer Country" value={data.manufacturerCountry} onChange={(v) => update('manufacturerCountry', v)} placeholder="e.g. China" />
        )}
      </FieldCard>

      {/* ── Regulatory IDs (conditional: US/CA + radio) ── */}
      {(showFccId || showIcId) && (
        <FieldCard
          title="Regulatory IDs"
          rationale={
            showFccId && showIcId
              ? 'FCC ID is required for wireless devices sold in the US (FCC Part 15). IC ID is required for wireless devices sold in Canada (ISED regulations).'
              : showFccId
                ? 'FCC ID is legally required for wireless/radio devices (Bluetooth, WiFi, RF) sold in the United States.'
                : 'IC ID is mandatory for wireless/radio devices sold in Canada under ISED regulations.'
          }
        >
          {showFccId && <FieldInput label="FCC ID" value={data.fccId} onChange={(v) => update('fccId', v)} placeholder="e.g. 2ABC-12345" error={isFieldError('fccId')} />}
          {showIcId && <FieldInput label="IC ID" value={data.icId} onChange={(v) => update('icId', v)} placeholder="e.g. 12345-ABC" error={isFieldError('icId')} />}
        </FieldCard>
      )}

      {/* ── EU Representative (conditional: EU market + CE product + non-EU manufacturer) ── */}
      {showEURep && (
        <FieldCard
          title="EU Authorised Representative"
          rationale="Per EU 2019/1020, non-EU manufacturers selling CE-regulated products (electronics, toys) in the EU must designate an EU Authorised Representative. The label must display the official EU REP symbol with contact details."
        >
          <FieldInput label="Company Name" value={data.ecRepName} onChange={(v) => update('ecRepName', v)} placeholder="Full legal name (as on Mandate Agreement)" error={isFieldError('ecRepName')} />
          <FieldInput label="EU Address" value={data.ecRepAddress} onChange={(v) => update('ecRepAddress', v)} placeholder="Street, postal code, city, EU country" error={isFieldError('ecRepAddress')} />
          <FieldInput label="Email" value={data.ecRepEmail} onChange={(v) => update('ecRepEmail', v)} placeholder="contact@example.com" />
          <FieldInput label="Phone" value={data.ecRepPhone} onChange={(v) => update('ecRepPhone', v)} placeholder="+49 123 456789 (optional if email provided)" />
        </FieldCard>
      )}

      {/* ── EU Importer (conditional: EU market + non-CE product + non-EU manufacturer) ── */}
      {showEUImporter && !data.euImporterSkipped && (
        <EUImporterCard data={data} update={update} onSkip={handleSkipImporter} isFieldError={isFieldError} />
      )}
      {showEUImporter && data.euImporterSkipped && (
        <EUImporterSkippedCard onUnskip={handleUnskipImporter} />
      )}

      {/* ── UK Responsible Person (conditional: UK market + UKCA regulated + non-UK manufacturer) ── */}
      {showUKRep && (
        <FieldCard
          title="UK Responsible Person"
          rationale="Non-UK manufacturers of UKCA-regulated products (electronics, toys, medical devices, PPE) selling in Great Britain must appoint a UK Responsible Person per UK Product Safety regulations."
        >
          <FieldInput label="Company Name" value={data.ukRepName} onChange={(v) => update('ukRepName', v)} placeholder="Full legal company name" error={isFieldError('ukRepName')} />
          <FieldInput label="UK Address" value={data.ukRepAddress} onChange={(v) => update('ukRepAddress', v)} placeholder="Street, postal code, city, UK" error={isFieldError('ukRepAddress')} />
        </FieldCard>
      )}

      {/* ── Optional Fields ── */}
      <FieldCard
        title="Other (Optional)"
        rationale="Safety warnings are required only for specific hazards (button cell batteries, choking hazards). UPC barcode is optional but commonly needed for retail distribution."
      >
        <SafetyWarningPresets data={data} onSelect={handlePresetSelect} />
        <FieldInput label="Safety Warning" value={data.safetyWarning} onChange={(v) => update('safetyWarning', v)} placeholder="Leave blank or type a custom warning" />
        <FieldInput label="UPC Code" value={data.upcCode} onChange={(v) => update('upcCode', v)} placeholder="12-digit barcode number" />
      </FieldCard>

      {/* Helpful tip */}
      <div className="flex items-start gap-2.5 px-3 py-3 bg-[#F9FAFB] rounded-lg border border-[#F0F0F0]">
        <svg className="size-4 text-[#A1A1AA] mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
        <p className="text-[12px] text-[#71717A] leading-relaxed">
          Once you've filled in your details, you can print or save the label as a PDF. Need a different size? Adjust it in the Label Size section at the bottom of the preview.
        </p>
      </div>

      {/* Verify button hidden for now */}
      {/* <button
        onClick={handleVerify}
        className={`w-full py-2.5 rounded-lg border text-[13px] font-medium cursor-pointer transition-colors flex items-center justify-center gap-2 ${
          verifyResult === 'pass'
            ? 'border-[#BBF7D0] bg-[#F0FDF4] text-[#16A34A]'
            : verifyResult === 'fail'
              ? 'border-[#FECACA] bg-[#FEF2F2] text-[#EF4444]'
              : 'border-[#E5E5E5] bg-white text-[#18181B] hover:bg-[#F5F5F5]'
        }`}
      >
        {verifyResult === 'pass' ? (
          <>
            <CheckCircleIcon className="size-4" />
            Verified
          </>
        ) : verifyResult === 'fail' ? (
          <>
            <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            {errorFields.size} missing field{errorFields.size !== 1 ? 's' : ''} — see above
          </>
        ) : (
          <>
            <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            Verify
          </>
        )}
      </button> */}
    </div>
  )
}

function ChatPanel() {
  const step = useAtomValue(LabelGeneratorStore.onboardingStepAtom)
  const labelType = useAtomValue(LabelGeneratorStore.labelTypeAtom)

  return (
    <div className="w-[340px] shrink-0 border-r border-[#E5E5E5] flex flex-col bg-white">
      <ChatMessages />
      <OnboardingPanel />
      {/* ChatInput hidden — users interact via inline field cards */}
      {/* {step === 'done' && <ChatInput />} */}
      {labelType !== 'upc' && (
        <div className="px-4 pb-3 text-[11px] text-[#A1A1AA]">
          Vertco can make mistakes. Verify compliance manually.
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Label Preview
// ============================================================================

// Compliance mark SVG icons
// CE marking — official asset
function CEMarkIcon({ className }: { className?: string }) {
  return <img src="/assets/ce-mark.jpg" alt="CE" className={className} />
}

// UKCA marking — official asset
function UKCAMarkIcon({ className }: { className?: string }) {
  return <img src="/assets/ukca.jpg" alt="UKCA" className={className} />
}

function BatteryWarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <rect x="6" y="4" width="12" height="17" rx="1" />
      <rect x="9" y="2" width="6" height="2" rx="0.5" />
      <line x1="12" y1="9" x2="12" y2="14" strokeWidth={2} />
      <line x1="9.5" y1="11.5" x2="14.5" y2="11.5" strokeWidth={2} />
    </svg>
  )
}

// Mobius loop recycling symbol — official asset
function RecyclingIcon({ className }: { className?: string }) {
  return <img src="/assets/recycling.jpg" alt="Recycling" className={className} />
}

// WEEE — crossed-out wheelie bin, official asset
function WEEEIcon({ className }: { className?: string }) {
  return <img src="/assets/weee.jpg" alt="WEEE" className={className} />
}

/**
 * UPC-A barcode renderer.
 * Accepts optional `barsWidth` and `barsHeight` (in design pixels) for explicit
 * sizing. When omitted, uses compact defaults suitable for the compliance label.
 * `digitScale` controls human-readable digit font size (default 1).
 */
function UPCBarcode({ code, barsWidth, barsHeight, digitScale = 1 }: {
  code: string
  barsWidth?: number
  barsHeight?: number
  digitScale?: number
}) {
  // UPC-A encoding tables
  const L_PATTERNS: Record<string, number[]> = {
    '0': [0,0,0,1,1,0,1], '1': [0,0,1,1,0,0,1], '2': [0,0,1,0,0,1,1],
    '3': [0,1,1,1,1,0,1], '4': [0,1,0,0,0,1,1], '5': [0,1,1,0,0,0,1],
    '6': [0,1,0,1,1,1,1], '7': [0,1,1,1,0,1,1], '8': [0,1,1,0,1,1,1],
    '9': [0,0,0,1,0,1,1],
  }
  const R_PATTERNS: Record<string, number[]> = {
    '0': [1,1,1,0,0,1,0], '1': [1,1,0,0,1,1,0], '2': [1,1,0,1,1,0,0],
    '3': [1,0,0,0,0,1,0], '4': [1,0,1,1,1,0,0], '5': [1,0,0,1,1,1,0],
    '6': [1,0,1,0,0,0,0], '7': [1,0,0,0,1,0,0], '8': [1,0,0,1,0,0,0],
    '9': [1,1,1,0,1,0,0],
  }

  // Pad/truncate to 12 digits
  const digits = code.replace(/\D/g, '').padEnd(12, '0').slice(0, 12)

  // Build bar pattern: start(101) + 6 left digits + mid(01010) + 6 right digits + end(101)
  const bars: number[] = [1, 0, 1]
  for (let i = 0; i < 6; i++) bars.push(...(L_PATTERNS[digits[i]] ?? L_PATTERNS['0']))
  bars.push(0, 1, 0, 1, 0)
  for (let i = 6; i < 12; i++) bars.push(...(R_PATTERNS[digits[i]] ?? R_PATTERNS['0']))
  bars.push(1, 0, 1)

  // 95 modules total. viewBox uses unit coordinates; rendered size set by width/height.
  const MODULES = 95
  const VIEW_H = 70 // viewBox height units (aspect ratio for bars)
  const svgW = barsWidth ?? MODULES * 1.5   // default ~143px for compliance label
  const svgH = barsHeight ?? 50              // default 50px for compliance label
  const fontSize = Math.ceil(10 * digitScale)

  return (
    <div className="flex flex-col items-center">
      <svg width={svgW} height={svgH} viewBox={`0 0 ${MODULES} ${VIEW_H}`} preserveAspectRatio="none">
        {bars.map((bar, i) =>
          bar === 1 ? (
            <rect key={i} x={i} y={0} width={1} height={VIEW_H} fill="#18181B" />
          ) : null
        )}
      </svg>
      <span
        className="tracking-[0.15em] text-[#18181B] font-mono mt-0.5"
        style={{ fontSize: `${fontSize}px` }}
      >
        {digits.slice(0, 1)} {digits.slice(1, 7)} {digits.slice(7, 12)} {digits.slice(11)}
      </span>
    </div>
  )
}

/**
 * UPC label content with GS1-compliant barcode sizing.
 * Default barcode: 1.47" × 1.021" (37.34mm × 25.93mm).
 * Minimum barcode: 1.20" × 0.82"  (30.48mm × 20.83mm).
 * All fonts ≥ 7pt (2.47mm) for legibility.
 * Content is vertically and horizontally centered with equal spacing.
 */
function UPCLabelContent() {
  const data = useAtomValue(LabelGeneratorStore.labelDataAtom)
  const dimensions = useAtomValue(LabelGeneratorStore.dimensionsAtom)

  // Design pixel conversion: 380px design width = dimensions.width mm
  const pxPerMm = 380 / dimensions.width
  // Explicit design height so justify-center works (h-full doesn't propagate through ShrinkToFit)
  const designHeight = dimensions.height * pxPerMm

  // 7pt minimum in design pixels: 7pt = 2.4694mm
  const MIN_FONT_PX = 2.4694 * pxPerMm

  // 1" minimum height for UPC labels (25.4mm)
  const MIN_LABEL_H_MM = 25.4
  const isBelowMinHeight = dimensions.height < MIN_LABEL_H_MM

  // Count how many text lines will appear below barcode
  const hasCountry = !!data.countryOfOrigin
  const hasInfo = !!data.additionalInfo
  const textLineCount = (hasCountry ? 1 : 0) + (hasInfo ? 1 : 0)

  // --- Dynamic barcode sizing: width-first, percentage-based height ---
  // 1. Fix barcode width at 80% of label width (10% margin each side)
  const barcodeW = 380 * 0.80

  // 2. Font sizes: 7pt minimum — barcode bars carry the data, text just needs legibility
  const fontPx = Math.ceil(MIN_FONT_PX)
  const digitFontPx = fontPx
  const countryFontPx = fontPx
  const infoFontPx = fontPx

  // 3. Gaps: small fixed values, tighter for smaller labels
  const heightRatio = Math.min(1, dimensions.height / 38.1)
  const gapPx = Math.max(2, Math.round(6 * heightRatio))
  const textGapPx = Math.max(1, Math.round(4 * heightRatio))

  // 4. Bar height: percentage-based to guarantee no overflow
  // More text lines → less room for bars
  const barPct = textLineCount === 0 ? 0.68 : textLineCount === 1 ? 0.58 : 0.48
  let barcodeH = Math.floor(designHeight * barPct)
  barcodeH = Math.max(barcodeH, 10)

  return (
    <div
      className="w-full flex flex-col items-center justify-center text-[#18181B]"
      style={{ height: Math.floor(designHeight) }}
    >
      {isBelowMinHeight ? (
        /* Below 1" height — show sizing warning */
        <div className="flex flex-col items-center gap-1 text-center px-4">
          <svg className="size-5 text-[#F59E0B]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
          <p style={{ fontSize: `${Math.max(11, MIN_FONT_PX)}px` }} className="text-[#92400E] font-medium">Label too small</p>
          <p style={{ fontSize: `${Math.max(10, MIN_FONT_PX * 0.9)}px` }} className="text-[#A1A1AA] leading-snug">
            Minimum height is 1″ (25.4mm) for UPC barcodes
          </p>
        </div>
      ) : (
        <>
          {/* UPC Barcode (centered, dynamically sized) */}
          {data.upcCode ? (
            <UPCBarcode code={data.upcCode} barsWidth={barcodeW} barsHeight={barcodeH} digitScale={fontPx / 10} />
          ) : (
            <div style={{ fontSize: `${MIN_FONT_PX}px` }} className="text-[#A1A1AA] italic">Enter UPC code on the left panel</div>
          )}

          {/* Country of Origin (below barcode) */}
          {hasCountry && (
            <p
              className="font-semibold text-center uppercase tracking-wide"
              style={{ fontSize: `${countryFontPx}px`, marginTop: `${gapPx}px` }}
            >
              Made in {data.countryOfOrigin}
            </p>
          )}

          {/* Additional Info (below country) */}
          {hasInfo && (
            <p
              className="text-[#3F3F46] text-center leading-snug"
              style={{ fontSize: `${infoFontPx}px`, marginTop: `${textGapPx}px` }}
            >
              {data.additionalInfo}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function LabelContent() {
  const data = useAtomValue(LabelGeneratorStore.labelDataAtom)
  const showCE = LabelGeneratorStore.shouldShowCE(data)
  const showUKCA = LabelGeneratorStore.shouldShowUKCA(data)
  const showCrossedBin = LabelGeneratorStore.shouldShowCrossedBin(data)
  const showFCC = LabelGeneratorStore.shouldShowFCC(data)
  const showIC = LabelGeneratorStore.shouldShowIC(data)
  const showFrancePackaging = LabelGeneratorStore.shouldShowFrancePackaging(data)
  const showItalyPackaging = LabelGeneratorStore.shouldShowItalyPackaging(data)
  const italyPackagingLines = LabelGeneratorStore.getItalyPackagingLines(data.frPackagingTemplate)
  const needsUKRep = LabelGeneratorStore.shouldRequireUKRep(data)

  // Show EU REP section when ecRep data exists; show Importer section when euImporter data exists
  // Data-driven display: the label renders whichever section has data, in the correct format
  const hasECRepData = !!(data.ecRepName)
  // Only show importer section when: name is provided AND product actually requires an
  // EU Importer (non-CE, EU market, non-EU manufacturer). This prevents the section
  // from appearing prematurely if the AI emits a stray euImporterName marker.
  const hasEUImporterData = !!(data.euImporterName) && !data.euImporterSkipped && LabelGeneratorStore.shouldRequireEUImporter(data)
  const hasRegulatoryIds = showFCC || showIC
  const showToyWarning = LabelGeneratorStore.shouldShowToyWarning(data)
  const toyWarningTexts = LabelGeneratorStore.getToyWarningTexts(data)

  return (
    <div className="w-full flex flex-col text-[#18181B] p-[3px]">
      {/* ══ Group 1: Product Identity ══ */}
      <div className="px-4 pt-3 pb-2">
        <h1 className="text-[18px] font-extrabold uppercase tracking-wider leading-[1.15] break-words">
          {data.productName || 'PRODUCT NAME'}
        </h1>
        {data.modelRef && (
          <div className="flex items-baseline gap-2 mt-1 flex-wrap">
            <span className="text-[11px] text-[#3F3F46] tracking-wide">REF {data.modelRef}</span>
          </div>
        )}
        {hasRegulatoryIds && (
          <div className="flex gap-4 flex-wrap mt-1.5">
            {showFCC && (
              <span className="text-[10px] font-mono tracking-wide">FCC ID: {data.fccId || '—'}</span>
            )}
            {showIC && (
              <span className="text-[10px] font-mono tracking-wide">IC: {data.icId || '—'}</span>
            )}
          </div>
        )}
      </div>

      {/* ══ Group 2: Entity Information (MFR / EC REP / UK REP) ══ */}
      <div className="mx-4 pt-2.5 pb-2 border-t border-[#71717A] space-y-2.5">
        {/* Manufacturer */}
        <div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold border border-[#18181B] px-[3px] py-px leading-none">MFR</span>
            <span className="text-[11px] font-semibold leading-tight">{data.manufacturerName || '—'}</span>
          </div>
          {data.manufacturerAddress && (
            <p className="text-[10px] text-[#3F3F46] leading-snug mt-0.5">{data.manufacturerAddress}</p>
          )}
        </div>

        {/* EU REP — shown when EC REP data exists, with official [EU | REP] symbol (same as online) */}
        {hasECRepData && (
          <div className="flex items-start gap-2.5">
            <img src="/assets/eu-rep.png" alt="EU REP" className="h-[48px] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold leading-tight">{data.ecRepName}</p>
              {data.ecRepAddress && (
                <p className="text-[10px] text-[#3F3F46] leading-snug mt-0.5">{data.ecRepAddress}</p>
              )}
              {data.ecRepEmail && (
                <p className="text-[10px] text-[#3F3F46] leading-snug">{data.ecRepEmail}</p>
              )}
              {data.ecRepPhone && (
                <p className="text-[10px] text-[#3F3F46] leading-snug">{data.ecRepPhone}</p>
              )}
            </div>
          </div>
        )}

        {/* EU Importer — shown when importer data exists, "Imported by:" prefix */}
        {hasEUImporterData && (
          <div>
            <p className="text-[10px] font-bold text-[#18181B]">Imported by:</p>
            <p className="text-[11px] font-semibold leading-tight mt-0.5">{data.euImporterName}</p>
            {data.euImporterAddress && (
              <p className="text-[10px] text-[#3F3F46] leading-snug mt-0.5">{data.euImporterAddress}</p>
            )}
          </div>
        )}

        {/* UK REP */}
        {needsUKRep && (
          <div className="flex items-start gap-2.5">
            <span className="text-[10px] font-bold border border-[#18181B] px-[3px] py-[2px] leading-none shrink-0 mt-0.5">UK REP</span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold leading-tight">{data.ukRepName || '—'}</p>
              {data.ukRepAddress && (
                <p className="text-[10px] text-[#3F3F46] leading-snug mt-0.5">{data.ukRepAddress}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ══ Group 3: Origin ══ */}
      <div className="mx-4 pt-2.5 pb-2 border-t border-[#71717A]">
        <p className="text-[11px] font-semibold leading-tight">
          Made in {data.countryOfOrigin || '—'}
        </p>
      </div>

      {/* ══ Group 4: Warnings ══ */}
      {(data.safetyWarning && !['none', 'n/a', 'na', '-', '—'].includes(data.safetyWarning.toLowerCase().trim())) || (showToyWarning && toyWarningTexts.length > 0) ? (
        <div className="mx-4 pt-1 pb-1.5 space-y-2">
          {/* Safety Warning */}
          {data.safetyWarning && !['none', 'n/a', 'na', '-', '—'].includes(data.safetyWarning.toLowerCase().trim()) && (
            <div className="border border-[#18181B] rounded px-2 py-1.5 flex items-center gap-2">
              <svg className="size-5 shrink-0 text-[#18181B]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
              <p className="text-[10px] leading-snug text-[#18181B]">
                <span className="font-bold">WARNING: </span>{data.safetyWarning}
              </p>
            </div>
          )}

          {/* Toy Warning */}
          {showToyWarning && toyWarningTexts.length > 0 && (
            <div className="border border-[#18181B] rounded px-2.5 py-2 space-y-2">
              {toyWarningTexts.map((tw, idx) => (
                <div key={idx}>
                  {tw.lines.map((line, i) => (
                    <p key={i} className={`text-[10px] leading-snug text-[#18181B] ${i === 0 ? 'font-bold' : ''}`}>
                      {line}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* ── Spacer: absorbs remaining vertical space ── */}
      <div className="flex-1 min-h-[4px]" />

      {/* ══ Group 5: Compliance Marks & Certifications ══ */}
      {/* Minimum physical sizes: CE ≥5mm, UKCA ≥5mm, Crossed-bin ≥5mm, Recycling ≥5mm */}
      {/* At DESIGN_WIDTH=380px for 100mm label: 1mm ≈ 3.8px. Using 7mm≈27px for comfortable compliance */}
      {(showCE || showUKCA || showCrossedBin || data.showRecycling || showFrancePackaging || (showItalyPackaging && italyPackagingLines.length > 0)) && (
        <div className="mx-4 pt-2 pb-2 border-t border-[#71717A]">
          {(showCE || showUKCA || showCrossedBin || data.showRecycling) && (
            <div className="flex items-center gap-3 flex-wrap">
              {showCE && <CEMarkIcon className="h-[27px]" />}
              {showUKCA && <UKCAMarkIcon className="h-[27px]" />}
              {showCrossedBin && <WEEEIcon className="h-[27px]" />}
              {data.showRecycling && <RecyclingIcon className="size-[22px]" />}
            </div>
          )}

          {/* France: Info-tri (includes Triman) */}
          {showFrancePackaging && (
            <div className={showCE || showUKCA || showCrossedBin || data.showRecycling ? 'mt-2' : ''}>
              {data.frPackagingTemplate === 'paper' && (
                <img src="/assets/info-tri-paper.png" alt="Info-tri Paper" className="h-[50px]" />
              )}
              {data.frPackagingTemplate === 'paper_plastic' && (
                <img src="/assets/info-tri-paper-plastic.png" alt="Info-tri Paper + Plastic" className="h-[50px]" />
              )}
              {data.frPackagingTemplate === 'plastic' && (
                <img src="/assets/info-tri-plastic.png" alt="Info-tri Plastic" className="h-[50px]" />
              )}
            </div>
          )}

          {/* Italy: Packaging material identification (D.Lgs 116/2020) */}
          {showItalyPackaging && italyPackagingLines.length > 0 && (
            <div className={showCE || showUKCA || showCrossedBin || data.showRecycling || showFrancePackaging ? 'mt-2' : ''}>
              <div className="space-y-1">
                {italyPackagingLines.map((line, idx) => (
                  <div key={idx} className="text-[10px] leading-snug text-[#3F3F46]">
                    <span className="font-semibold text-[#18181B]">{line.material}</span>
                    {' '}<span className="font-mono">{line.code}</span>
                    {' — '}{line.collection}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ Group 6: UPC Barcode ══ */}
      {data.upcCode && (
        <div className={`mx-4 pb-2.5 pt-2 flex justify-center ${!(showCE || showUKCA || showCrossedBin || data.showRecycling || showFrancePackaging || (showItalyPackaging && italyPackagingLines.length > 0)) ? 'border-t border-[#71717A]' : ''}`}>
          <UPCBarcode code={data.upcCode} />
        </div>
      )}
    </div>
  )
}

function ShrinkToFit({ maxHeight, minShrink = 0, children }: { maxHeight: number; minShrink?: number; children: React.ReactNode }) {
  const innerRef = useRef<HTMLDivElement>(null)
  const [shrink, setShrink] = useState(1)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    if (!innerRef.current) return
    const observer = new ResizeObserver(() => {
      const el = innerRef.current
      if (!el) return
      const natural = el.scrollHeight
      if (natural > maxHeight && maxHeight > 0) {
        const ideal = maxHeight / natural
        // Clamp to minShrink so fonts/icons stay above minimum physical sizes
        const clamped = minShrink > 0 ? Math.max(minShrink, ideal) : ideal
        setShrink(clamped)
        // Content overflows even at minimum allowed shrink
        setIsOverflowing(clamped > ideal)
      } else {
        setShrink(1)
        setIsOverflowing(false)
      }
    })
    observer.observe(innerRef.current)
    return () => observer.disconnect()
  }, [maxHeight, minShrink])

  return (
    <div data-shrinktofit="" style={{ height: maxHeight, overflow: 'hidden', position: 'relative' }}>
      <div
        data-shrinktofit-inner=""
        ref={innerRef}
        style={{
          transformOrigin: 'top left',
          transform: shrink < 1 ? `scale(${shrink})` : undefined,
          width: shrink < 1 ? `${100 / shrink}%` : '100%',
        }}
      >
        {children}
      </div>
      {isOverflowing && (
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-red-100 to-transparent flex items-end justify-center pb-0.5">
          <span className="text-[9px] text-red-600 font-medium">Content exceeds label — increase label size</span>
        </div>
      )}
    </div>
  )
}

function ScaledLabel({ targetWidth, targetHeight }: { targetWidth: number; targetHeight: number }) {
  // Strategy: render the label at a fixed design width with height proportional to target aspect ratio,
  // then scale uniformly to fit within the target box. LabelContent uses flex layout to fill space.
  // If content overflows, ShrinkToFit uniformly scales it down to fit.
  const dimensions = useAtomValue(LabelGeneratorStore.dimensionsAtom)
  const labelType = useAtomValue(LabelGeneratorStore.labelTypeAtom)
  const DESIGN_WIDTH = 380
  const designHeight = DESIGN_WIDTH * (targetHeight / targetWidth)
  const scale = targetWidth / DESIGN_WIDTH

  // minShrink: ensure compliance minimum sizes after scaling
  // Design: smallest font = 10px. Physical size = 10 * shrink * (labelWidthMm / DESIGN_WIDTH) mm
  // Requirement: ≥ 7pt = 2.47mm → shrink ≥ 93.86 / labelWidthMm
  // For icons (smallest 22px, min 5mm): shrink ≥ 86.36 / labelWidthMm (less restrictive)
  const minShrink = Math.min(1, 93.86 / dimensions.width)

  return (
    <div
      id="print-label"
      style={{
        width: targetWidth,
        height: targetHeight,
        position: 'relative',
      }}
    >
      {/* Outer border at the target size */}
      <div
        className="absolute inset-0 border-2 border-[#18181B] bg-white"
        style={{ zIndex: 0 }}
      />
      {/* Scaled content fills the entire label area */}
      <div
        id="print-label-content"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: DESIGN_WIDTH,
          height: designHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          zIndex: 1,
        }}
      >
        <ShrinkToFit maxHeight={designHeight} minShrink={minShrink}>
          {labelType === 'upc' ? <UPCLabelContent /> : <LabelContent />}
        </ShrinkToFit>
      </div>
    </div>
  )
}

function PreviewTitle() {
  const source = useAtomValue(LabelGeneratorStore.labelSourceAtom)

  if (source === 'amazon') {
    return (
      <div className="flex items-center gap-1.5">
        <Link
          to="/amazon/listings"
          className="text-sm font-medium text-[#18181B] hover:underline"
        >
          Amazon Listings
        </Link>
        <svg className="size-3 text-[#A1A1AA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
        <span className="text-sm font-medium text-[#18181B]">Label Preview</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-[#18181B]">Label Preview</span>
    </div>
  )
}

function PreviewArea() {
  const [zoom, setZoom] = useAtom(LabelGeneratorStore.zoomAtom)
  const dimensions = useAtomValue(LabelGeneratorStore.dimensionsAtom)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      }
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  // Convert mm to px (approx 3.78 px/mm at 96dpi)
  const pxPerMm = 3.78
  const labelWidthPx = dimensions.width * pxPerMm
  const labelHeightPx = dimensions.height * pxPerMm

  const widthCm = (dimensions.width / 10).toFixed(1)
  const widthIn = (dimensions.width / 25.4).toFixed(2)
  const heightCm = (dimensions.height / 10).toFixed(1)
  const heightIn = (dimensions.height / 25.4).toFixed(2)

  // Apply zoom: 100% = actual pixel size at 96dpi
  const zoomFactor = zoom / 100
  const displayWidth = labelWidthPx * zoomFactor
  const displayHeight = labelHeightPx * zoomFactor

  // Calculate fit-to-screen zoom
  const handleFit = useCallback(() => {
    const padding = 80
    const availW = containerSize.width - padding
    const availH = containerSize.height - padding
    const fitPct = Math.min(availW / labelWidthPx, availH / labelHeightPx) * 100
    setZoom(Math.round(Math.max(10, Math.min(300, fitPct))))
  }, [containerSize, labelWidthPx, labelHeightPx, setZoom])

  // Default to fit view on initial render and whenever dimensions change
  const lastFitDims = useRef<string>('')
  useEffect(() => {
    if (containerSize.width <= 100 || containerSize.height <= 100) return
    const dimKey = `${dimensions.width}x${dimensions.height}`
    if (lastFitDims.current !== dimKey) {
      lastFitDims.current = dimKey
      handleFit()
    }
  }, [containerSize, dimensions.width, dimensions.height, handleFit])

  const zoomIn = useCallback(() => {
    setZoom((z: number) => Math.min(300, z + 25))
  }, [setZoom])

  const zoomOut = useCallback(() => {
    setZoom((z: number) => Math.max(10, z - 25))
  }, [setZoom])

  return (
    <div className="flex-1 flex flex-col bg-[#FAFAFA] min-w-0">
      {/* Preview header */}
      <div className="h-10 px-5 flex items-center justify-between shrink-0 border-b border-[#E5E5E5]">
        <PreviewTitle />
        <div className="flex items-center gap-1">
          <button
            onClick={handleFit}
            className="h-7 px-2.5 rounded text-xs font-medium flex items-center gap-1 cursor-pointer transition-colors text-[#52525B] hover:bg-[#F0F0F0]"
          >
            <MaximizeIcon className="size-3" />
            Fit
          </button>
          <div className="w-px h-4 bg-[#E5E5E5] mx-1" />
          <button
            onClick={zoomOut}
            disabled={zoom <= 10}
            className="size-7 rounded flex items-center justify-center text-[#52525B] hover:bg-[#F0F0F0] disabled:opacity-30 cursor-pointer transition-colors"
          >
            <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" d="M5 12h14" />
            </svg>
          </button>
          <button
            onClick={() => setZoom(100)}
            className="h-7 min-w-[48px] rounded text-xs font-medium text-[#18181B] cursor-pointer hover:bg-[#F0F0F0] transition-colors"
          >
            {zoom}%
          </button>
          <button
            onClick={zoomIn}
            disabled={zoom >= 300}
            className="size-7 rounded flex items-center justify-center text-[#52525B] hover:bg-[#F0F0F0] disabled:opacity-30 cursor-pointer transition-colors"
          >
            <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" d="M12 5v14m-7-7h14" />
            </svg>
          </button>
        </div>
      </div>

      {/* Label canvas */}
      <div ref={containerRef} className="flex-1 overflow-auto flex items-center justify-center p-10 relative">
        <div className="relative" style={{ padding: '32px 0 0 40px' }}>
          {/* Width dimension line — above the label */}
          <div className="absolute left-[40px] right-0 top-0 h-[24px] pointer-events-none" style={{ zIndex: 10 }}>
            <div className="absolute left-0 top-[4px] w-px h-[16px] bg-[#A1A1AA]" />
            <div className="absolute right-0 top-[4px] w-px h-[16px] bg-[#A1A1AA]" />
            <div className="absolute left-0 right-0 top-[11px] h-px bg-[#A1A1AA]" />
            <div className="absolute left-0 right-0 -top-[2px] flex justify-center">
              <span className="bg-[#FAFAFA] px-2 text-[11px] text-[#A1A1AA] whitespace-nowrap">
                {widthCm} cm / {widthIn}"
              </span>
            </div>
          </div>

          {/* Height dimension line — left of the label */}
          <div className="absolute left-0 top-[32px] bottom-0 w-[32px] pointer-events-none" style={{ zIndex: 10 }}>
            <div className="absolute top-0 right-0 h-px w-[16px] bg-[#A1A1AA]" />
            <div className="absolute bottom-0 right-0 h-px w-[16px] bg-[#A1A1AA]" />
            <div className="absolute top-0 bottom-0 right-[7px] w-px bg-[#A1A1AA]" />
            <div className="absolute top-0 bottom-0 right-0 w-[16px] flex items-center justify-center">
              <span className="bg-[#FAFAFA] py-1 text-[11px] text-[#A1A1AA] whitespace-nowrap -rotate-90">
                {heightCm} cm / {heightIn}"
              </span>
            </div>
          </div>

          {/* The label itself */}
          <div>
            <ScaledLabel targetWidth={displayWidth} targetHeight={displayHeight} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Bottom Bar
// ============================================================================

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  )
}

function UnlockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  )
}

function PackageDimensions() {
  const [dimensions, setDimensions] = useAtom(LabelGeneratorStore.dimensionsAtom)
  const [unit, setUnit] = useAtom(LabelGeneratorStore.dimensionUnitAtom)
  const [locked, setLocked] = useState(true)

  const mmToIn = (mm: number) => +(mm / 25.4).toFixed(3)
  const inToMm = (inches: number) => +(inches * 25.4).toFixed(2)

  // Display value: convert from internal mm to current unit
  const displayW = unit === 'in' ? mmToIn(dimensions.width) : dimensions.width
  const displayH = unit === 'in' ? mmToIn(dimensions.height) : dimensions.height

  // Local string state for each input so the cursor can move freely while typing.
  const [widthStr, setWidthStr] = useState(String(displayW))
  const [heightStr, setHeightStr] = useState(String(displayH))

  // Keep local strings in sync when the atom or unit changes externally
  useEffect(() => { setWidthStr(String(unit === 'in' ? mmToIn(dimensions.width) : dimensions.width)) }, [dimensions.width, unit])
  useEffect(() => { setHeightStr(String(unit === 'in' ? mmToIn(dimensions.height) : dimensions.height)) }, [dimensions.height, unit])

  const commitWidth = (str: string) => {
    const n = parseFloat(str)
    if (!isNaN(n) && n > 0) {
      const mm = unit === 'in' ? inToMm(n) : n
      setDimensions({ ...dimensions, width: mm })
    } else setWidthStr(String(displayW)) // revert on invalid
  }
  const commitHeight = (str: string) => {
    const n = parseFloat(str)
    if (!isNaN(n) && n > 0) {
      const mm = unit === 'in' ? inToMm(n) : n
      setDimensions({ ...dimensions, height: mm })
    } else setHeightStr(String(displayH)) // revert on invalid
  }

  const unitLabel = unit === 'in' ? 'in' : 'mm'

  return (
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-semibold tracking-wider text-[#52525B]">LABEL SIZE</h3>
        <button
          onClick={() => setLocked(!locked)}
          className="size-6 rounded flex items-center justify-center text-[#A1A1AA] hover:text-[#52525B] hover:bg-[#F0F0F0] cursor-pointer transition-colors"
          title={locked ? 'Unlock dimensions' : 'Lock dimensions'}
        >
          {locked ? <LockIcon className="size-3.5" /> : <UnlockIcon className="size-3.5" />}
        </button>
        {/* Unit toggle — placed next to lock for proximity */}
        <div className="flex rounded-md border border-[#E5E5E5] overflow-hidden text-[11px] font-medium">
          <button
            onClick={() => setUnit('mm')}
            className={`px-2 py-0.5 cursor-pointer transition-colors ${unit === 'mm' ? 'bg-[#18181B] text-white' : 'bg-white text-[#71717A] hover:bg-[#F5F5F5]'}`}
          >mm</button>
          <button
            onClick={() => setUnit('in')}
            className={`px-2 py-0.5 cursor-pointer transition-colors ${unit === 'in' ? 'bg-[#18181B] text-white' : 'bg-white text-[#71717A] hover:bg-[#F5F5F5]'}`}
          >in</button>
        </div>
      </div>
      <div className="flex gap-4">
        <div>
          <label className="block text-[11px] text-[#A1A1AA] mb-1">WIDTH ({unitLabel})</label>
          <input
            type="text"
            inputMode="decimal"
            value={widthStr}
            onChange={(e) => {
              const v = e.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
              setWidthStr(v)
              const n = parseFloat(v)
              if (!isNaN(n) && n > 0) {
                const mm = unit === 'in' ? inToMm(n) : n
                setDimensions({ ...dimensions, width: mm })
              }
            }}
            onBlur={() => commitWidth(widthStr)}
            disabled={locked}
            className={`h-10 px-3 border border-[#E5E5E5] rounded-md text-sm w-48 outline-none ${locked ? 'bg-[#F5F5F5] text-[#A1A1AA] cursor-not-allowed' : 'bg-[#FAFAFA] text-[#18181B] focus:border-[#A1A1AA]'}`}
          />
        </div>
        <div>
          <label className="block text-[11px] text-[#A1A1AA] mb-1">HEIGHT ({unitLabel})</label>
          <input
            type="text"
            inputMode="decimal"
            value={heightStr}
            onChange={(e) => {
              const v = e.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
              setHeightStr(v)
              const n = parseFloat(v)
              if (!isNaN(n) && n > 0) {
                const mm = unit === 'in' ? inToMm(n) : n
                setDimensions({ ...dimensions, height: mm })
              }
            }}
            onBlur={() => commitHeight(heightStr)}
            disabled={locked}
            className={`h-10 px-3 border border-[#E5E5E5] rounded-md text-sm w-48 outline-none ${locked ? 'bg-[#F5F5F5] text-[#A1A1AA] cursor-not-allowed' : 'bg-[#FAFAFA] text-[#18181B] focus:border-[#A1A1AA]'}`}
          />
        </div>
      </div>
    </div>
  )
}

function ComplianceSummary() {
  const data = useAtomValue(LabelGeneratorStore.labelDataAtom)
  const isCompliant = useAtomValue(LabelGeneratorStore.isCompliantAtom)
  const hasStarted = useAtomValue(LabelGeneratorStore.hasStartedAtom)
  const errors = useAtomValue(LabelGeneratorStore.validationErrorsAtom)

  // Build marks list from rule engine
  const marks = [
    LabelGeneratorStore.shouldShowCE(data) && 'CE',
    LabelGeneratorStore.shouldShowUKCA(data) && 'UKCA',
    LabelGeneratorStore.shouldShowCrossedBin(data) && 'Crossed Bin',
    LabelGeneratorStore.shouldShowFCC(data) && 'FCC',
    LabelGeneratorStore.shouldShowIC(data) && 'IC',
    LabelGeneratorStore.shouldShowTriman(data) && 'Triman',
    LabelGeneratorStore.shouldShowFrancePackaging(data) && 'Info-tri',
    LabelGeneratorStore.shouldShowToyWarning(data) && 'Toy Warning',
    data.showRecycling && 'Recycling ♻️',
  ].filter(Boolean)

  const statusText = !hasStarted
    ? 'Not started'
    : isCompliant
      ? 'Ready'
      : `${errors.length} issue${errors.length !== 1 ? 's' : ''}`

  const statusColor = !hasStarted
    ? 'text-[#A1A1AA]'
    : isCompliant
      ? 'text-[#16A34A]'
      : 'text-[#EF4444]'

  return (
    <div className="w-96 shrink-0">
      <h3 className="text-xs font-semibold tracking-wider text-[#52525B] mb-3">COMPLIANCE SUMMARY</h3>
      <div className="space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-[#52525B]">Markets</span>
          <span className="text-[#18181B] font-medium">
            {data.targetMarkets.length > 0 ? data.targetMarkets.join(', ') : '—'}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#52525B]">Product Type</span>
          <span className="text-[#18181B] font-medium">{data.productType.replace(/_/g, ' ') || '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#52525B]">Required Marks</span>
          <span className="text-[#18181B] font-medium">{marks.length > 0 ? marks.join(', ') : '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#52525B]">Status</span>
          <span className={`font-medium ${statusColor}`}>{statusText}</span>
        </div>
      </div>
    </div>
  )
}

function FeedbackPopover() {
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleSubmit = async () => {
    if (!message.trim() || status === 'sending') return
    setStatus('sending')
    try {
      const res = await fetch(apiBase + '/api/FeedbackStore/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: message.trim(), page: 'label-generator' }),
      })
      if (res.ok) {
        trackFeedbackSubmitted()
        setStatus('sent')
        setMessage('')
        setTimeout(() => {
          setStatus('idle')
          setIsOpen(false)
        }, 1500)
      } else {
        setStatus('idle')
      }
    } catch {
      setStatus('idle')
    }
  }

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-4 h-9 border border-[#E5E5E5] rounded-lg text-sm font-medium text-[#18181B] flex items-center gap-2 hover:bg-[#FAFAFA] transition-colors cursor-pointer"
      >
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
        </svg>
        Feedback
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg border border-[#E5E5E5] shadow-lg p-4 z-50">
          <p className="text-sm font-medium text-[#18181B] mb-1">Share your feedback</p>
          <p className="text-[11px] text-[#A1A1AA] mb-2">Or reach us at <a href="mailto:hello@vertco.io" className="text-[#3B82F6] hover:underline">hello@vertco.io</a></p>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tell us what you think..."
            rows={3}
            className="w-full px-3 py-2 border border-[#E5E5E5] rounded-md text-sm bg-[#FAFAFA] text-[#18181B] outline-none focus:border-[#A1A1AA] resize-none"
            disabled={status === 'sending'}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit()
              }
              if (e.key === 'Escape') {
                setIsOpen(false)
                setMessage('')
              }
            }}
            autoFocus
          />
          <div className="flex justify-end mt-2">
            {status === 'sent' ? (
              <span className="text-sm text-[#16A34A] font-medium">Sent!</span>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!message.trim() || status === 'sending'}
                className="h-8 px-4 rounded-md bg-[#18181B] text-white text-sm font-medium hover:bg-[#27272A] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                {status === 'sending' ? 'Sending...' : 'Send'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function BottomBar() {
  const labelType = useAtomValue(LabelGeneratorStore.labelTypeAtom)

  return (
    <div className="px-5 py-4 border-t border-[#E5E5E5] flex items-start gap-8 shrink-0 bg-white">
      <PackageDimensions />
      {labelType !== 'upc' && <ComplianceSummary />}
    </div>
  )
}

// ============================================================================
// Page
// ============================================================================

export default function LabelGeneratorPage() {
  return (
    <AppShell activeNav="label" topBarActions={<LabelTopBarActions />}>
      <div className="flex-1 flex min-h-0">
        <ChatPanel />
        <PreviewArea />
      </div>
      <BottomBar />
    </AppShell>
  )
}
